import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import HeroCarouselItem, {
  HeroCarouselItemProps,
} from 'components/helpers/CarouselItem/HeroCarouselItem';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { pushCarouselClickDataLayerObject } from 'lib/util/gtm-datalayer';
import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { classnames } from 'tailwindcss-classnames';
import HeroCarouselOneNorthDemoNotReadyForProd from './_HeroCarousel_OneNorthDemoNotReadyForProd';
import { env } from 'lib/env';

export type HeroCarouselProps = Sitecore.Override.ComponentBase &
  Components.Heroes.HeroCarousel.Fields.HeroCarousel;

const HeroCarouselImpl = ({ uid, rendering, fields }: HeroCarouselProps): JSX.Element => {
  if (!fields) {
    return <MissingDataSource />;
  }

  // WT-8259:Extracting the unique identifier for the component instance to ensure that Swiper controls are correctly associated with their respective carousels
  const findUid = uid ? uid : rendering.uid;
  const swiperControlId = findUid ? `ds-${findUid}` : `hero-carousel`;

  const carouselItems = fields.carouselItems.map((item) => {
    return {
      ...item,
    } as HeroCarouselItemProps;
  });

  let clickAction = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRealIndexChange = async (swiper: any) => {
    // eslint-disable-next-line no-promise-executor-return
    await new Promise((r) => setTimeout(r, 200));
    pushCarouselClickDataLayerObject(swiper.realIndex, clickAction, 'hero carousel', carouselItems);
    clickAction = '';
  };
  const handlePrev = () => {
    clickAction = 'carousel - left click';
  };
  const handleNext = () => {
    clickAction = 'carousel - right click';
  };

  return (
    <section
      data-component="authorable/Heroes/heroCarousel"
      id={swiperControlId}
      className="hero-carousel"
    >
      <div className={classnames('my-10', 'md:my-16')}>
        <Swiper
          loop={carouselItems.length > 1}
          slidesPerView={1}
          spaceBetween={0}
          navigation={{
            prevEl: `#${swiperControlId} .swiper-button-prev`,
            nextEl: `#${swiperControlId} .swiper-button-next`,
          }}
          pagination={{
            clickable: true,
            el: `#${swiperControlId} .swiper-pagination`,
            bulletElement: 'button',
          }}
          onRealIndexChange={handleRealIndexChange}
        >
          {carouselItems.map((carouselItem, index) => (
            <SwiperSlide key={carouselItem.id}>
              <HeroCarouselItem {...carouselItem} index={index} carouselName={carouselItem.name} />
            </SwiperSlide>
          ))}
          {carouselItems.length > 1 && (
            <div slot="container-end" className={classnames('mt-10')}>
              <div className={classnames('m-auto', 'flex', 'h-6', 'w-max', 'items-center')}>
                <button type="button" className="swiper-button-prev" onClick={handlePrev}>
                  prev
                </button>
                <div className="swiper-pagination" />
                <button type="button" className="swiper-button-next" onClick={handleNext}>
                  next
                </button>
              </div>
            </div>
          )}
        </Swiper>
      </div>
    </section>
  );
};

const HeroCarousel = (props: HeroCarouselProps): JSX.Element => {
  return env.FEATURE_FLAG_ENABLE_ONENORTH_DEMO ? (
    <HeroCarouselOneNorthDemoNotReadyForProd {...props} />
  ) : (
    <HeroCarouselImpl {...props} />
  );
};

export default HeroCarousel;
